import { useEffect } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { Form } from "../../components/Form";
import { Input } from "../../components/Input";
import { SelectInput } from "../../components/SelectInput";
import { SearchableSelect } from "../../components/SearchableSelect";
import { Toggle } from "../../components/Toggle";
import { MoneyInput } from "../../components/MoneyInput";
import { spacing } from "@styling/sizing";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import type {
  RestCreateModifier,
  RestModifier,
} from "@KassaroAB/shared/types/modifier";
import type { Money } from "@KassaroAB/shared/types/money";
import { FormSection } from "../../components/FormSection";
import { useCatalogScope } from "../../hooks/useCatalogScope";
import { useGetBookkeepingAccountsQuery } from "../../query/bookkeepingAccountsQuery";
import { useGetProductGroupsQuery } from "../../query/productGroupsQuery";

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const VAT_OPTIONS = [25, 12, 6, 0] as const;

const moneyMinorToMajor = (amount: number | undefined) =>
  amount != null ? amount / 100 : 0;

const toMoney = (amount: number): Money | null => {
  if (!amount) return null;
  return { amount: Math.round(amount * 100), currency: Currency.SEK };
};

interface ModifierFormProps {
  formId: string;
  modifier?: RestModifier;
  savedTick?: number;
  onSubmit: (data: RestCreateModifier) => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

export const ModifierForm = ({
  formId,
  modifier,
  savedTick,
  onSubmit,
  onDirtyChange,
}: ModifierFormProps) => {
  const { t } = useTranslation();
  const { scope } = useCatalogScope();
  const { data: productGroups } = useGetProductGroupsQuery(scope);
  const { data: bookkeepingAccounts } = useGetBookkeepingAccountsQuery(scope);

  const form = useForm({
    defaultValues: {
      name: modifier?.name ?? "",
      priceAmount: moneyMinorToMajor(modifier?.price?.amount),
      productGroupId: modifier?.productGroupId ?? null,
      VAT: modifier?.VAT ?? null,
      bookkeepingAccountId: modifier?.bookkeepingAccountId ?? null,
      internal: modifier?.internal ?? true,
      maxQuantity:
        modifier?.maxQuantity !== null && modifier?.maxQuantity !== undefined
          ? String(modifier.maxQuantity)
          : "",
      slots:
        modifier?.slots !== null && modifier?.slots !== undefined
          ? String(modifier.slots)
          : "",
      takeawayPriceAmount: moneyMinorToMajor(modifier?.takeawayPrice?.amount),
      takeawayVAT: modifier?.takeawayVAT ?? null,
      takeawayBookkeepingAccountId:
        modifier?.takeawayBookkeepingAccountId ?? null,
    },
    onSubmit: ({ value }) => {
      const price = toMoney(value.priceAmount);
      const maxQuantity = Math.floor(Number(value.maxQuantity));
      const slots = Math.floor(Number(value.slots));
      const group = productGroups?.find(
        (item) => item.id === value.productGroupId,
      );
      const submitVatLocked =
        group?.VAT !== null && group?.VAT !== undefined;
      const submitAccountLocked =
        group?.bookkeepingAccountId !== null &&
        group?.bookkeepingAccountId !== undefined;
      onSubmit({
        name: value.name,
        price,
        productGroupId: value.productGroupId,
        VAT: submitVatLocked && group ? group.VAT : value.VAT,
        bookkeepingAccountId: submitAccountLocked
          ? (group?.bookkeepingAccountId ?? null)
          : value.bookkeepingAccountId,
        internal: price ? false : value.internal,
        maxQuantity: maxQuantity > 1 ? maxQuantity : null,
        slots: slots > 1 ? slots : null,
        takeawayPrice: toMoney(value.takeawayPriceAmount),
        takeawayVAT: value.takeawayVAT,
        takeawayBookkeepingAccountId: value.takeawayBookkeepingAccountId,
      });
    },
  });

  const isDirty = useStore(form.store, (state) => !state.isDefaultValue);
  const productGroupId = useStore(
    form.store,
    (state) => state.values.productGroupId,
  );
  const priceAmount = useStore(form.store, (state) => state.values.priceAmount);
  const groupsLoaded = productGroups !== undefined;
  const selectedGroup = productGroups?.find(
    (group) => group.id === productGroupId,
  );
  const vatLocked =
    groupsLoaded &&
    selectedGroup?.VAT !== null &&
    selectedGroup?.VAT !== undefined;
  const accountLocked =
    groupsLoaded &&
    selectedGroup?.bookkeepingAccountId !== null &&
    selectedGroup?.bookkeepingAccountId !== undefined;
  const internalLocked = !!priceAmount;

  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!savedTick) return;
    form.reset(form.state.values);
  }, [savedTick, form]);

  return (
    <Form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <FormSection title={t("modifierForm.basicInformation")}>
        <Fields>
          <form.Field name="name">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("modifierForm.name")}</FormLabel>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  required
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="priceAmount">
            {(field) => (
              <MoneyInput
                label={t("modifierForm.price")}
                amount={field.state.value}
                currency={Currency.SEK}
                onAmountChange={(amount) =>
                  field.handleChange(Number.isFinite(amount) ? amount : 0)
                }
                onAmountBlur={field.handleBlur}
              />
            )}
          </form.Field>
          <form.Field name="internal">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("modifierForm.internal")}</FormLabel>
                <Toggle
                  checked={internalLocked ? false : field.state.value}
                  onChange={field.handleChange}
                  onBlur={field.handleBlur}
                  disabled={internalLocked}
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="maxQuantity">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("modifierForm.maxQuantity")}</FormLabel>
                <Input
                  type="number"
                  min={1}
                  step={1}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t("productForm.notSet")}
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="slots">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("modifierForm.slots")}</FormLabel>
                <Input
                  type="number"
                  min={1}
                  step={1}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="1"
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="productGroupId">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("productForm.productGroup")}</FormLabel>
                <SearchableSelect
                  value={field.state.value ?? ""}
                  options={(productGroups ?? []).map((group) => ({
                    value: group.id,
                    label: group.name,
                  }))}
                  onChange={(groupId) => field.handleChange(groupId || null)}
                  onBlur={field.handleBlur}
                  placeholder={t("productForm.none")}
                  emptyOptionLabel={t("productForm.none")}
                  noResultsLabel={t("common.noMatches")}
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="VAT">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("productForm.vat")}</FormLabel>
                <SelectInput
                  value={
                    vatLocked
                      ? String(selectedGroup?.VAT ?? "")
                      : (field.state.value ?? "")
                  }
                  onChange={(e) =>
                    field.handleChange(
                      e.target.value === "" ? null : Number(e.target.value),
                    )
                  }
                  onBlur={field.handleBlur}
                  disabled={!groupsLoaded || vatLocked}
                >
                  <option value="">{t("productForm.notSet")}</option>
                  {VAT_OPTIONS.map((vat) => (
                    <option key={vat} value={String(vat)}>
                      {vat}%
                    </option>
                  ))}
                </SelectInput>
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="bookkeepingAccountId">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("productForm.bookkeepingAccountId")}</FormLabel>
                <SelectInput
                  value={
                    accountLocked
                      ? (selectedGroup?.bookkeepingAccountId ?? "")
                      : (field.state.value ?? "")
                  }
                  onChange={(e) =>
                    field.handleChange(e.target.value || null)
                  }
                  onBlur={field.handleBlur}
                  disabled={!groupsLoaded || accountLocked}
                >
                  <option value="">{t("productForm.none")}</option>
                  {bookkeepingAccounts?.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.accountNumber} — {account.name}
                    </option>
                  ))}
                </SelectInput>
              </FormGroup>
            )}
          </form.Field>
        </Fields>
      </FormSection>

      <FormSection title={t("productForm.sections.takeawayTitle")}>
        <Fields>
          <form.Field name="takeawayPriceAmount">
            {(field) => (
              <MoneyInput
                label={t("productForm.takeawayPrice")}
                amount={field.state.value}
                currency={Currency.SEK}
                onAmountChange={(amount) =>
                  field.handleChange(Number.isFinite(amount) ? amount : 0)
                }
                onAmountBlur={field.handleBlur}
              />
            )}
          </form.Field>
          <form.Field name="takeawayVAT">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("productForm.takeawayVat")}</FormLabel>
                <SelectInput
                  value={field.state.value ?? ""}
                  onChange={(e) =>
                    field.handleChange(
                      e.target.value === "" ? null : Number(e.target.value),
                    )
                  }
                  onBlur={field.handleBlur}
                >
                  <option value="">{t("productForm.notSet")}</option>
                  {VAT_OPTIONS.map((vat) => (
                    <option key={vat} value={String(vat)}>
                      {vat}%
                    </option>
                  ))}
                </SelectInput>
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="takeawayBookkeepingAccountId">
            {(field) => (
              <FormGroup>
                <FormLabel>
                  {t("productForm.takeawayBookkeepingAccount")}
                </FormLabel>
                <SelectInput
                  value={field.state.value ?? ""}
                  onChange={(e) =>
                    field.handleChange(e.target.value || null)
                  }
                  onBlur={field.handleBlur}
                >
                  <option value="">{t("productForm.none")}</option>
                  {bookkeepingAccounts?.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.accountNumber} — {account.name}
                    </option>
                  ))}
                </SelectInput>
              </FormGroup>
            )}
          </form.Field>
        </Fields>
      </FormSection>
    </Form>
  );
};
